"use client";

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Sparkles } from "lucide-react";
import type { Gift } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  type CoupleWrappedExperienceData,
} from "@/data/experienceDataSchema";

export type CoupleWrappedVariant = "default" | "spotify";

interface CoupleWrappedPalette {
  accent: string;
  accentRgb: string;
}

interface CoupleWrappedSectionProps {
  gift: Partial<Gift>;
  variant: CoupleWrappedVariant;
  palette?: CoupleWrappedPalette;
  className?: string;
}

interface CoupleStats {
  totalDays: number;
  totalHours: number;
  totalMinutes: number;
  totalSeconds: number;
  years: number;
  months: number;
  days: number;
}

function parseCoupleStartDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function computeStats(start: Date, now: Date): CoupleStats {
  const diffMs = Math.max(0, now.getTime() - start.getTime());
  const totalSeconds = Math.floor(diffMs / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();

  if (days < 0) {
    months -= 1;
    const previousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += previousMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) {
    years = 0;
    months = 0;
    days = 0;
  }

  return { totalDays, totalHours, totalMinutes, totalSeconds, years, months, days };
}

function formatNumber(value: number): string {
  return value.toLocaleString("pt-BR");
}

function buildCoupleNames(gift: Partial<Gift>): string | null {
  const a = gift.partnerName?.trim();
  const b = gift.recipientName?.trim();
  if (a && b) return `${a} & ${b}`;
  if (a) return a;
  if (b) return b;
  return null;
}

function buildSinceLabel(date: Date): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function CoupleWrappedSection({
  gift,
  variant,
  palette,
  className,
}: CoupleWrappedSectionProps) {
  const customData = useMemo(
    () => getExperienceData<CoupleWrappedExperienceData>(gift, "couple-wrapped"),
    [gift]
  );

  // Prioriza a data personalizada; cai para o coupleStartDate do gift.
  const effectiveStartRaw =
    customData?.startDate?.trim() || gift.coupleStartDate;

  const startDate = useMemo(
    () => parseCoupleStartDate(effectiveStartRaw),
    [effectiveStartRaw]
  );

  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    if (!startDate) return;
    setNow(new Date());
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [startDate]);

  const stats = useMemo(() => {
    if (!startDate || !now) return null;
    return computeStats(startDate, now);
  }, [startDate, now]);

  const coupleNames = buildCoupleNames(gift);
  const sinceLabel = startDate ? buildSinceLabel(startDate) : null;
  const headline = customData?.headline?.trim();
  const subtitle = customData?.subtitle?.trim();
  const highlightMessage = customData?.highlightMessage?.trim();

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        stats={stats}
        coupleNames={coupleNames}
        sinceLabel={sinceLabel}
        hasStartDate={Boolean(startDate)}
        className={className}
        headline={headline}
        subtitle={subtitle}
        highlightMessage={highlightMessage}
      />
    );
  }

  return (
    <DefaultVariant
      stats={stats}
      coupleNames={coupleNames}
      sinceLabel={sinceLabel}
      hasStartDate={Boolean(startDate)}
      className={className}
      headline={headline}
      subtitle={subtitle}
      highlightMessage={highlightMessage}
    />
  );
}

// ─── Variante default — clara, romântica, premium ───────────────────────────

interface VariantInnerProps {
  stats: CoupleStats | null;
  coupleNames: string | null;
  sinceLabel: string | null;
  hasStartDate: boolean;
  className?: string;
  headline?: string;
  subtitle?: string;
  highlightMessage?: string;
}

function DefaultVariant({
  stats,
  coupleNames,
  sinceLabel,
  hasStartDate,
  className,
  headline,
  subtitle,
  highlightMessage,
}: VariantInnerProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "couple-wrapped-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <DecorativeHearts variant="default" />

      <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Heart className="h-3 w-3 fill-[#E11D48] text-[#E11D48]" />
            Tempo de casal
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {headline || "Nosso tempo juntos"}
          </h2>
          {coupleNames ? (
            <p className="text-sm text-[#881337]/80">{coupleNames}</p>
          ) : null}
        </header>

        {hasStartDate && stats ? (
          <>
            <p className="text-center text-sm sm:text-base text-[#1F2937]/80 leading-relaxed">
              {subtitle ? (
                subtitle
              ) : (
                <>
                  Estamos juntos há{" "}
                  <strong className="text-[#E11D48] font-semibold">
                    {formatNumber(stats.totalDays)} dias
                  </strong>{" "}
                  — uma história que continua sendo escrita a cada segundo.
                </>
              )}
            </p>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCardLight value={formatNumber(stats.totalDays)} label="Dias" />
              <StatCardLight
                value={formatNumber(stats.totalHours)}
                label="Horas"
              />
              <StatCardLight
                value={formatNumber(stats.totalMinutes)}
                label="Minutos"
              />
              <StatCardLight
                value={formatNumber(stats.totalSeconds)}
                label="Segundos"
                pulse
              />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm text-[#1F2937]/75">
              <span className="rounded-full bg-white/60 px-3 py-1 shadow-sm">
                {stats.years > 0
                  ? `${stats.years} ${stats.years === 1 ? "ano" : "anos"}`
                  : "Menos de um ano"}
              </span>
              <span className="rounded-full bg-white/60 px-3 py-1 shadow-sm">
                {stats.months} {stats.months === 1 ? "mês" : "meses"}
              </span>
              <span className="rounded-full bg-white/60 px-3 py-1 shadow-sm">
                {stats.days} {stats.days === 1 ? "dia" : "dias"}
              </span>
            </div>

            {sinceLabel ? (
              <p className="text-center text-xs uppercase tracking-[0.2em] text-[#881337]/70">
                Desde {sinceLabel}
              </p>
            ) : null}

            {highlightMessage ? (
              <p
                className="text-center text-sm italic text-[#881337]/85 leading-relaxed max-w-md mx-auto"
                style={{ fontFamily: "var(--font-serif, serif)" }}
              >
                {highlightMessage}
              </p>
            ) : null}
          </>
        ) : (
          <EmptyState variant="default" />
        )}
      </div>
    </motion.section>
  );
}

function StatCardLight({
  value,
  label,
  pulse,
}: {
  value: string;
  label: string;
  pulse?: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-white/80 bg-white/75 px-3 py-4 text-center shadow-sm backdrop-blur transition-transform hover:-translate-y-0.5">
      <motion.span
        key={pulse ? value : undefined}
        initial={pulse ? { scale: 1.08, opacity: 0.85 } : false}
        animate={pulse ? { scale: 1, opacity: 1 } : undefined}
        transition={pulse ? { duration: 0.4, ease: "easeOut" } : undefined}
        className="block text-xl sm:text-2xl font-bold tabular-nums text-[#E11D48]"
      >
        {value}
      </motion.span>
      <span className="mt-1 text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-[#881337]/80">
        {label}
      </span>
    </div>
  );
}

// ─── Variante Spotify — imersiva, escura, premium ───────────────────────────

interface SpotifyVariantProps extends VariantInnerProps {
  palette: CoupleWrappedPalette;
}

function SpotifyVariant({
  palette,
  stats,
  coupleNames,
  sinceLabel,
  hasStartDate,
  className,
  headline,
  subtitle,
  highlightMessage,
}: SpotifyVariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "couple-wrapped-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <DecorativeHearts variant="spotify" accent={palette.accent} />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <Heart className="h-3.5 w-3.5" fill="currentColor" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Tempo de casal
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {headline || "Nosso tempo juntos"}
          </h3>
          {coupleNames ? (
            <p className="text-[11px] text-white/55 tracking-wide">
              {coupleNames}
            </p>
          ) : null}
        </div>

        {hasStartDate && stats ? (
          <>
            <p className="text-[12px] text-white/75 leading-relaxed">
              {subtitle ? (
                subtitle
              ) : (
                <>
                  Já são{" "}
                  <strong className="text-white font-semibold">
                    {formatNumber(stats.totalDays)}
                  </strong>{" "}
                  dias compartilhando momentos — uma retrospectiva ao vivo da
                  nossa história.
                </>
              )}
            </p>

            <div className="grid grid-cols-2 gap-2">
              <StatCardDark
                value={formatNumber(stats.totalDays)}
                label="Dias"
                palette={palette}
              />
              <StatCardDark
                value={formatNumber(stats.totalHours)}
                label="Horas"
                palette={palette}
              />
              <StatCardDark
                value={formatNumber(stats.totalMinutes)}
                label="Minutos"
                palette={palette}
              />
              <StatCardDark
                value={formatNumber(stats.totalSeconds)}
                label="Segundos"
                palette={palette}
                pulse
              />
            </div>

            <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-white/65">
              <span
                className="rounded-full px-2 py-0.5"
                style={{
                  background: `rgba(${palette.accentRgb},0.14)`,
                  border: `1px solid rgba(${palette.accentRgb},0.22)`,
                }}
              >
                {stats.years > 0
                  ? `${stats.years} ${stats.years === 1 ? "ano" : "anos"}`
                  : "Menos de um ano"}
              </span>
              <span
                className="rounded-full px-2 py-0.5"
                style={{
                  background: `rgba(${palette.accentRgb},0.14)`,
                  border: `1px solid rgba(${palette.accentRgb},0.22)`,
                }}
              >
                {stats.months} {stats.months === 1 ? "mês" : "meses"}
              </span>
              <span
                className="rounded-full px-2 py-0.5"
                style={{
                  background: `rgba(${palette.accentRgb},0.14)`,
                  border: `1px solid rgba(${palette.accentRgb},0.22)`,
                }}
              >
                {stats.days} {stats.days === 1 ? "dia" : "dias"}
              </span>
            </div>

            {sinceLabel ? (
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                Desde {sinceLabel}
              </p>
            ) : null}

            {highlightMessage ? (
              <p className="text-[11px] italic text-white/75 leading-relaxed">
                {highlightMessage}
              </p>
            ) : null}
          </>
        ) : (
          <EmptyState variant="spotify" accent={palette.accent} />
        )}
      </div>
    </motion.section>
  );
}

function StatCardDark({
  value,
  label,
  palette,
  pulse,
}: {
  value: string;
  label: string;
  palette: CoupleWrappedPalette;
  pulse?: boolean;
}) {
  return (
    <div
      className="rounded-xl px-3 py-3 text-center backdrop-blur"
      style={{
        background: `rgba(${palette.accentRgb},0.08)`,
        border: `1px solid rgba(${palette.accentRgb},0.18)`,
      }}
    >
      <motion.span
        key={pulse ? value : undefined}
        initial={pulse ? { scale: 1.08, opacity: 0.85 } : false}
        animate={pulse ? { scale: 1, opacity: 1 } : undefined}
        transition={pulse ? { duration: 0.4, ease: "easeOut" } : undefined}
        className="block text-lg sm:text-xl font-bold tabular-nums text-white"
      >
        {value}
      </motion.span>
      <span
        className="mt-0.5 block text-[9px] font-bold uppercase tracking-[0.18em]"
        style={{ color: palette.accent }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── Componentes auxiliares ────────────────────────────────────────────────

function EmptyState({
  variant,
  accent,
}: {
  variant: CoupleWrappedVariant;
  accent?: string;
}) {
  if (variant === "spotify") {
    return (
      <div className="flex flex-col items-center gap-2 py-2 text-center">
        <Sparkles className="h-5 w-5" style={{ color: accent ?? "#1DB954" }} />
        <p className="text-[12px] text-white/70 leading-relaxed">
          Cada segundo dessa história merece ser lembrado.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <Sparkles className="h-6 w-6 text-[#E11D48]" />
      <p className="text-sm text-[#1F2937]/75">
        Cada segundo dessa história merece ser lembrado.
      </p>
    </div>
  );
}

function DecorativeHearts({
  variant,
  accent,
}: {
  variant: CoupleWrappedVariant;
  accent?: string;
}) {
  if (variant === "spotify") {
    return (
      <>
        <Heart
          className="absolute -top-2 -right-2 h-16 w-16 opacity-15"
          style={{ color: accent ?? "#1DB954" }}
          fill="currentColor"
        />
        <Heart
          className="absolute bottom-3 -left-3 h-10 w-10 opacity-10"
          style={{ color: accent ?? "#1DB954" }}
          fill="currentColor"
        />
      </>
    );
  }
  return (
    <>
      <Heart
        className="absolute -top-4 -right-4 h-24 w-24 text-[#E11D48]/15"
        fill="currentColor"
      />
      <Heart
        className="absolute -bottom-3 -left-3 h-14 w-14 text-[#F59E0B]/25"
        fill="currentColor"
      />
      <Sparkles className="absolute top-4 left-4 h-4 w-4 text-[#E11D48]/40" />
    </>
  );
}
